import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight,
  Wrench,
  Thermometer,
  Home as HomeIcon,
  Zap,
  PaintBucket,
  Truck,
  Sun,
  Bug,
  DoorOpen,
  Lock,
  Fan,
  Container,
  Toilet,
  Plane,
  Droplets,
  Wind,
  Trash2,
  Bath,
  Settings,
  Search,
  Square,
  Flame,
  Clock,
  MapPin,
  DollarSign,
  CheckCircle,
  Phone,
  TrendingUp,
  Filter,
  AlertTriangle,
  Download,
  FileSpreadsheet
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getOfferIcon } from "../utils/offerIcons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Fallback offers (used if backend API returns empty/fails). Same shape as API response.
const FALLBACK_OFFERS = [
  { name: "Water & Fire Damage", icon: "Droplets", category: "Home Services", payout: "515", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Restoration", popular: true, color: "from-red-500 to-orange-500", zipcode_sheet_id: null },
  { name: "Roofers", icon: "Home", category: "Home Services", payout: "178", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: true, color: "from-blue-500 to-cyan-500", zipcode_sheet_id: "1G9rBXpaMYDLmgvvhp-xxeA8Cp0_r1TLwxCP2U9ByQaA" },
  { name: "Plumbers", icon: "Wrench", category: "Home Services", payout: "98.50", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: true, color: "from-blue-600 to-blue-400", zipcode_sheet_id: "1d4nred2XsHoSbcpDshPYSZhhm7WsH8INzonRizWA8ZY" },
  { name: "Heating Contractors", icon: "Flame", category: "Home Services", payout: "96", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: true, color: "from-orange-500 to-yellow-500", zipcode_sheet_id: null },
  { name: "HVAC", icon: "Thermometer", category: "Home Services", payout: "94", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: true, color: "from-cyan-500 to-blue-500", zipcode_sheet_id: "16SCSF9GklxRscwvpp09qyaG51Xmzr4zQ" },
  { name: "Garage Doors", icon: "DoorOpen", category: "Home Services", payout: "77", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: false, color: "from-slate-500 to-slate-400", zipcode_sheet_id: "1BhnlMIp_0GokJ-Tm0xUvgWX2nXF1rnm0JIzwRi6D3Oo" },
  { name: "Electricians", icon: "Zap", category: "Home Services", payout: "57", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: true, color: "from-yellow-500 to-amber-500", zipcode_sheet_id: "1GSfkIKlOaiDDfN4LPSn5APlCIHqtbVO8RUXPlY0WtEw" },
  { name: "Windows Installers", icon: "Square", category: "Home Services", payout: "56", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Installation", popular: false, color: "from-sky-500 to-blue-500", zipcode_sheet_id: "14BxvPtAj6nbwC9l9UDaxpXTBfIF62fhNu8-2vkYd-48" },
  { name: "Pest Control", icon: "Bug", category: "Home Services", payout: "56", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Extermination", popular: false, color: "from-green-600 to-green-400", zipcode_sheet_id: "1wUd7FIJ1WvkL8TT-thW1KUgWp1SclKZcjUEdvApdQ7I" },
  { name: "Bathroom Remodeling", icon: "Bath", category: "Home Services", payout: "54", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Remodeling", popular: false, color: "from-purple-500 to-pink-500", zipcode_sheet_id: "1b4IjUDN3BP-pd9atMaEDhLsHYh5scGYEQi4-uclvgxw" },
  { name: "Solar Installation", icon: "Sun", category: "Home Services", payout: "53", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Installation", popular: true, color: "from-yellow-400 to-orange-400", zipcode_sheet_id: "1wOdAt018ee7oQFMZdp5MSJcF2AhyVPr4XQk6nHi7Ypk" },
  { name: "Appliance Repair", icon: "Settings", category: "Home Services", payout: "36", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair", popular: false, color: "from-gray-500 to-gray-400", zipcode_sheet_id: "1_EYExT_w3vtmqM_psokwraWBz2YWYb1ScNOjYxBXxOA" },
  { name: "Painters", icon: "PaintBucket", category: "Home Services", payout: "34", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Painting", popular: false, color: "from-pink-500 to-rose-500", zipcode_sheet_id: "10rE-XJuY18xHVFKkqh7HvMbFbq7VulcaG8KKwUNtM64" },
  { name: "Gutters", icon: "Wind", category: "Home Services", payout: "30", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Repair & Cleaning", popular: false, color: "from-teal-500 to-cyan-500", zipcode_sheet_id: "1Xunucsvx5QVrdFlNsghLhzIgPyCJW74ScUNHugPLca8" },
  { name: "Junk Removal", icon: "Trash2", category: "Home Services", payout: "10", is_fixed: true, buffer_time: "260", payment_terms: "Net 10", call_type: "Removal", popular: false, color: "from-amber-600 to-yellow-500", zipcode_sheet_id: "13RPeTZ1PSG_s89pLU8CF27khh2EJpGs-HcjmZEt9AUs" },
  { name: "Movers", icon: "Truck", category: "Home Services", payout: "23", is_fixed: false, buffer_time: "120", payment_terms: "Net 10", call_type: "Moving Services", popular: true, color: "from-indigo-500 to-purple-500", zipcode_sheet_id: "1RCAlzOa4XWBFE-B1fkHrGCg8bR07edQrB2X1Lx7DVbs" },
  { name: "Locksmith", icon: "Lock", category: "Emergency", payout: "20", is_fixed: false, buffer_time: "90-120", payment_terms: "Net 10", call_type: "Emergency Lockout", popular: false, color: "from-slate-600 to-slate-500", zipcode_sheet_id: "17N8ecOavK7Cin0K597UChxZHIRGFcA94YpKeA8ZYNek" },
  { name: "Flight Booking", icon: "Plane", category: "Travel", payout: "20", is_fixed: true, buffer_time: "60", payment_terms: "Net 7", call_type: "Booking", popular: false, color: "from-sky-600 to-blue-500", zipcode_sheet_id: null },
  { name: "Chimney Services", icon: "Flame", category: "Home Services", payout: "15", is_fixed: true, buffer_time: "100", payment_terms: "Net 10", call_type: "Cleaning & Repair", popular: false, color: "from-stone-500 to-stone-400", zipcode_sheet_id: null },
  { name: "Air Duct Cleaning", icon: "Fan", category: "Home Services", payout: "15", is_fixed: true, buffer_time: "100", payment_terms: "Net 10", call_type: "Cleaning", popular: false, color: "from-emerald-500 to-teal-500", zipcode_sheet_id: null },
  { name: "Dumpster Rentals", icon: "Container", category: "Rentals", payout: "10", is_fixed: true, buffer_time: "60", payment_terms: "Net 7", call_type: "Rental", popular: false, color: "from-zinc-500 to-zinc-400", zipcode_sheet_id: null },
  { name: "Porta Potty Rentals", icon: "Toilet", category: "Rentals", payout: "10", is_fixed: true, buffer_time: "60", payment_terms: "Net 7", call_type: "Rental", popular: false, color: "from-blue-400 to-cyan-400", zipcode_sheet_id: null }
];

const categories = ["All", "Home Services", "Emergency", "Travel", "Rentals"];

const OffersPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopularOnly, setShowPopularOnly] = useState(false);
  const [offers, setOffers] = useState(FALLBACK_OFFERS);

  // Fetch live offers from backend; if empty / fails, keep fallback list.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/offers`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setOffers(data);
        }
      })
      .catch(() => {
        // silent fallback
      });
    return () => { cancelled = true; };
  }, []);

  // Highest-paying first. Fixed-payout offers are typically low-value ($10-$20)
  // so this naturally floats the big performance-based verticals to the top.
  const sortByPayoutDesc = (a, b) => {
    const pa = parseFloat(String(a.payout || 0).replace(/[^\d.]/g, "")) || 0;
    const pb = parseFloat(String(b.payout || 0).replace(/[^\d.]/g, "")) || 0;
    return pb - pa;
  };

  const filteredOffers = offers
    .filter((o) => {
      const matchesCategory = selectedCategory === "All" || o.category === selectedCategory;
      const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPopular = !showPopularOnly || o.popular;
      return matchesCategory && matchesSearch && matchesPopular;
    })
    .sort(sortByPayoutDesc);

  // Grouped view is used only for the default "All / no search / no filter"
  // state — that's when a categorised layout adds value. Everything else stays
  // as a flat sorted grid (existing UX for searches / category tabs).
  const isDefaultView =
    selectedCategory === "All" && searchTerm.trim() === "" && !showPopularOnly;

  const popularOffers = filteredOffers.filter((o) => o.popular).slice(0, 8);

  // Build ordered category groups. Order preserves the `categories` array so
  // Home Services always appears first, Emergency next, etc.
  const groupedOffers = categories
    .filter((c) => c !== "All")
    .map((cat) => ({
      category: cat,
      items: filteredOffers.filter((o) => o.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  const categoryMeta = {
    "Home Services": { icon: HomeIcon, blurb: "Repair, install & maintenance verticals with the highest volume." },
    "Emergency": { icon: AlertTriangle, blurb: "High-urgency, high-intent calls that convert on the first ring." },
    "Travel": { icon: Plane, blurb: "Booking-driven verticals for licensed travel agencies." },
    "Rentals": { icon: Container, blurb: "Short-cycle rental verticals with steady daily demand." },
  };

  return (
    <div className="min-h-screen bg-slate-950 font-['Inter']" data-testid="offers-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700/30 via-transparent to-slate-800/20"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 mb-6">
              <TrendingUp className="w-4 h-4 text-slate-300" />
              <span className="text-slate-300 font-medium text-sm">100+ Home Services Offers</span>
            </motion.div>
            
            <motion.h1 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Premium Pay-Per-Call <span className="gradient-text">Offers</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#e2e8f0] text-lg max-w-2xl mx-auto mb-10">
              Partner with us to access high-converting verticals with competitive payouts up to $515 per call. USA Nationwide coverage.
            </motion.p>
            
            {/* Stats Row — Courses-page card style */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-purple-500/25">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-extrabold gradient-text">$515</p>
                <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Max Payout</p>
              </div>
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-purple-500/25">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-extrabold gradient-text">100+</p>
                <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Active Offers</p>
              </div>
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-purple-500/25">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-extrabold gradient-text">$50</p>
                <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Min Payment Threshold</p>
              </div>
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-purple-500/25">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <p className="text-base font-extrabold gradient-text leading-tight">Weekly Net 7</p>
                <p className="text-base font-extrabold gradient-text leading-tight">Monthly Net 10</p>
                <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Payment Options</p>
              </div>
              <div className="p-4 text-center">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-2 shadow-lg shadow-purple-500/25">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-extrabold gradient-text">USA</p>
                <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Nationwide</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FRAUD WARNING NOTICE - Sleek Banner */}
      <section className="bg-slate-900 border-b border-slate-800" data-testid="fraud-warning">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-full border border-slate-700">
              <AlertTriangle className="w-4 h-4 text-slate-300" />
              <span className="text-slate-200 text-xs font-semibold uppercase tracking-wide">Quality Policy</span>
            </div>
            <p className="text-slate-300 text-sm">
              <span className="text-white font-medium">Zero tolerance</span> for fraud/fake/massage/spa/IVR manipulation calls. 
              <span className="text-slate-100 font-medium ml-1">Violation = Instant block & payment forfeiture.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Bar */}
      <section className="py-4 sm:py-6 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800 sticky top-16 z-40" data-testid="filters">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-center lg:justify-between">
            {/* Search + Best Coverage Zips CTA */}
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full lg:w-auto lg:flex-1 lg:max-w-2xl">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search offers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 sm:py-3 rounded-xl border border-slate-700 bg-slate-800/70 text-slate-100 placeholder-slate-500 focus:bg-slate-800 focus:border-slate-500 focus:ring-1 focus:ring-slate-500/40 outline-none transition-all"
                  data-testid="search-input"
                />
              </div>
              <Link
                to="/target-zips"
                data-testid="best-coverage-zips-btn"
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-white hover:bg-slate-200 text-slate-900 font-semibold text-sm shadow-lg shadow-slate-950/40 hover:-translate-y-0.5 transition-all"
              >
                <MapPin className="w-4 h-4" strokeWidth={2.5} />
                Best Coverage Zips
                <ArrowRight className="w-4 h-4 opacity-90 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
              </Link>
            </div>

            {/* Filters — one horizontal-scroll row on mobile, wrap freely on lg+ */}
            <div className="flex items-center gap-2 overflow-x-auto lg:overflow-visible lg:flex-wrap -mx-3 sm:-mx-4 lg:mx-0 px-3 sm:px-4 lg:px-0 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* Popular Toggle */}
              <button
                onClick={() => setShowPopularOnly(!showPopularOnly)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium text-sm whitespace-nowrap flex-shrink-0 transition-all ${
                  showPopularOnly
                    ? "bg-white text-slate-900"
                    : "bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white"
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Popular
              </button>

              {/* Category Filters */}
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
                  className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium text-sm whitespace-nowrap flex-shrink-0 transition-all ${
                    selectedCategory === category
                      ? "bg-white text-slate-900"
                      : "bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results Count — hidden in grouped default view since each section shows its own count */}
      {!isDefaultView && (
        <section className="py-4 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-slate-500 text-sm">
              Showing <span className="font-semibold text-slate-900">{filteredOffers.length}</span> offers
              {selectedCategory !== "All" && <span> in <span className="font-semibold text-slate-900">{selectedCategory}</span></span>}
              <span className="text-slate-400"> · sorted by highest payout</span>
            </p>
          </div>
        </section>
      )}

      {/* Offers — either grouped (default view) or a flat filtered grid */}
      <section className="py-8 bg-white" data-testid="offers-grid">
        <div className="max-w-7xl mx-auto px-4">
          {filteredOffers.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No offers found matching your criteria.</p>
              <button
                onClick={() => { setSearchTerm(""); setSelectedCategory("All"); setShowPopularOnly(false); }}
                className="mt-4 text-blue-600 font-medium hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : isDefaultView ? (
            <>
              {/* HOT OFFERS SPOTLIGHT — a curated row of top popular offers */}
              {popularOffers.length > 0 && (
                <div className="mb-14" data-testid="hot-offers-spotlight">
                  <SectionHeader
                    icon={TrendingUp}
                    title="Top Offers Right Now"
                    subtitle="Highest-converting verticals across our network — priority payouts."
                    count={popularOffers.length}
                  />
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {popularOffers.map((offer, index) => (
                      <OfferCard key={offer.id || offer.name} offer={offer} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {/* CATEGORY SECTIONS */}
              {groupedOffers.map(({ category, items }) => {
                const meta = categoryMeta[category] || { icon: HomeIcon, blurb: "" };
                return (
                  <div key={category} className="mb-14" data-testid={`category-section-${category.toLowerCase().replace(/\s/g, '-')}`}>
                    <SectionHeader
                      icon={meta.icon}
                      title={category}
                      subtitle={meta.blurb}
                      count={items.length}
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {items.map((offer, index) => (
                        <OfferCard key={offer.id || offer.name} offer={offer} index={index} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredOffers.map((offer, index) => (
                <OfferCard key={offer.id || offer.name} offer={offer} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden" data-testid="offers-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-blue-800/10"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Start Earning?
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
              Join our network of successful publishers and start promoting high-converting offers today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/apply" 
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-10 py-4 rounded-xl inline-flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-600/25"
              >
                Publisher Sign Up <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/buyer" 
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-10 py-4 rounded-xl inline-flex items-center justify-center gap-2 border border-slate-700 transition-all"
              >
                Buyer Sign Up <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// ---------- Reusable Section Header ----------
const SectionHeader = ({ icon: Icon, title, subtitle, count }) => (
  <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
    <div className="flex items-start gap-4 min-w-0">
      <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-blue-600" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{title}</h2>
        {subtitle && <p className="text-slate-500 text-sm mt-1 leading-relaxed max-w-xl">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2 text-sm text-slate-500 flex-shrink-0">
      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 border border-blue-200 font-semibold text-blue-700 tabular-nums">
        {count} {count === 1 ? "offer" : "offers"}
      </span>
    </div>
  </div>
);

// ---------- Reusable Offer Card ----------
const OfferCard = ({ offer, index }) => {
  const Icon = getOfferIcon(offer.icon);
  const isFixed = offer.is_fixed ?? offer.isFixed;
  const bufferTime = offer.buffer_time ?? offer.bufferTime;
  const paymentTerms = offer.payment_terms ?? offer.paymentTerms;
  const callType = offer.call_type ?? offer.callType;
  const trafficSources = offer.traffic_sources || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index % 8) * 0.03 }}
      className="relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col"
      data-testid={`offer-card-${offer.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      {/* Hero band — blue to purple gradient, fixed height so all cards align */}
      <div className="relative bg-gradient-to-br from-[#3a56c4] to-[#2743b2] px-4 py-3 overflow-hidden h-[112px] flex flex-col justify-between">
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-20 h-20 rounded-full bg-white/5 pointer-events-none" />

        <div className="relative flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/25 flex-shrink-0">
            <Icon className="w-4 h-4 text-white" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-white text-sm leading-tight line-clamp-2">{offer.name}</h3>
            <span className="inline-flex mt-1 items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/15 border border-white/25 text-white">
              <span className="w-1 h-1 rounded-full bg-white flex-shrink-0" />
              {offer.category}
            </span>
          </div>
        </div>

        <div>
          {!isFixed && <p className="relative text-white/60 text-[9px] font-bold uppercase tracking-widest mb-0.5">Up to</p>}
          <div className="relative flex items-baseline gap-2">
            <span className="text-white font-bold text-2xl leading-none tabular-nums drop-shadow-sm">
              ${offer.payout}
            </span>
            {isFixed && (
              <span className="text-[9px] font-bold uppercase tracking-widest text-white/85 px-1.5 py-0.5 rounded bg-white/15 border border-white/25">
                Fixed
              </span>
            )}
          </div>
          <p className="relative text-white/70 text-[10px] mt-0.5 font-medium">per qualified call</p>
        </div>
      </div>

      {/* Body — full metadata & CTA */}
      <div className="p-4 flex flex-col flex-1">
        {/* Stat rows — boxed pills like the reference design */}
        <div className="space-y-1.5 mb-3">
          <StatRow icon={Clock} label="Buffer" value={`${bufferTime} sec post IVR`} />
          <StatRow icon={DollarSign} label="Payment" value={paymentTerms} />
          <StatRow icon={Phone} label="Call type" value={callType} />
        </div>

        {/* Traffic sources — uniform pill grid */}
        {trafficSources.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> Traffic Sources
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {trafficSources.map((t) => (
                <span
                  key={t}
                  title={t}
                  className="text-[10px] font-semibold bg-white border border-slate-300 text-slate-800 px-2 py-1 rounded-md text-center truncate"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Spacer so the CTA always sits at the bottom of the card */}
        <div className="flex-1" />

        {/* Apply CTA — dark */}
        <Link
          to="/apply"
          className="mt-1 w-full bg-slate-900 hover:bg-black text-white font-semibold py-2.5 text-sm rounded-xl inline-flex items-center justify-center gap-2 transition-all"
        >
          Apply Now
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
        </Link>
      </div>
    </motion.div>
  );
};

const StatRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex-shrink-0">
      <Icon className="w-3.5 h-3.5 text-[#3a56c4]" strokeWidth={2.25} />
      {label}
    </span>
    <span className="text-xs font-bold text-slate-800 text-right leading-tight break-words">
      {value}
    </span>
  </div>
);

export default OffersPage;
